import { IntegrationData, IntegrationType, ModuleData, ModuleType } from './Types';

export const ModulesData: Record<ModuleType, ModuleData> = {
    [ModuleType.authenticationFirewall]: {
        label: 'Authentication Firewall',
        color: '#E78F27',
        isDisabled: false,
        title: 'Authentication Firewall',
        metrics: {
            activePolicies: { value: 0, label: 'Active Policies' },
            protectedUsers: { value: 0, label: 'Protected Users' },
            deniedAuthentications: { value: 0, label: 'Denied Authentications' }
        },
        info: {
            summary: 'Boost your resilience with identity-based Zero Trust policies.',
            description:
                'Make unauthorized access and lateral movement impossible with an Authentication Firewall that controls access from behind your Active Directory, stopping attacks before they can spread.',
            funFact: '80% of lateral movement and ransomware attacks use compromised identities',
            url: 'https://www.silverfort.com/firewall-signup/'
        }
    },
    [ModuleType.mfaUsers]: {
        label: 'Universal MFA',
        color: '#F18930',
        isDisabled: false,
        title: 'Universal Multi-Factor Authentication (MFA)',
        metrics: {
            protectedUsers: { value: 0, label: 'Protected Users' },
            mfaPolicies: { value: 0, label: 'MFA Policies' },
            mfaRequests: { value: 0, label: 'MFA Requests' }
        },
        info: {
            summary: "Enable MFA for any resource, including 'unprotectable' systems.",
            description:
                'Address security and compliance gaps by enabling MFA anywhere. Our patented technology integrates with AD and extends MFA even to legacy apps, command line interfaces and OT systems - without modifying them.',
            funFact: "MFA stops 99.9% of account compromise attacks, but isn't available for many critical systems",
            url: 'https://www.silverfort.com/mfa-signup/'
        }
    },
    [ModuleType.nhiVisibility]: {
        label: 'NHI Security',
        color: '#E39222',
        isDisabled: false,
        title: 'Non-Human Identity Security',
        metrics: {
            nhis: { value: 0, label: 'Total NHI Accounts' },
            protectedNHIs: { value: 0, label: 'Protected NHI Accounts' },
            nhisAtRisk: { value: 0, label: 'NHI Accounts at Risk' }
        },
        info: {
            summary: 'Discover, analyze, and protect every Non-Human Identity.',
            description:
                'Map and secure your NHIs, from AD service accounts to cloud-native tokens and keys, across all on-prem and cloud environments. This includes the unique ability to control where each service account can be used.',
            funFact: 'Most companies have 10x-50x more NHIs than human users',
            url: 'https://www.silverfort.com/nhi-signup/'
        }
    },
    [ModuleType.privilegedAccessSecurity]: {
        label: 'Privileged Access Security',
        color: '#ED8B2D',
        isDisabled: false,
        title: 'Privileged Access Security (PAS)',
        metrics: {
            privilegedAccounts: { value: 0, label: 'Privileged Accounts' },
            crossTierAuthentications: { value: 0, label: 'Cross Tier Authentications' },
            protectedPrivilegedAccounts: { value: 0, label: 'Protected Privileged Accounts' }
        },
        info: {
            summary: 'Go beyond managing your privileged accounts. Secure them.',
            description:
                'Discover and protect privileged accounts rapidly. Our patented technology finds privileged accounts automatically, and enables Least Privilege and Just-In-Time (JIT) access policies in an innovative, frictionless manner.',
            funFact: 'Compromised privileged accounts are involved in more than 70% of all data breaches',
            url: 'http://www.silverfort.com/pas-signup/'
        }
    },
    [ModuleType.identityInventory]: {
        label: 'Identity Graph and Inventory',
        color: '#D79B16',
        isDisabled: false,
        title: 'Identity Graph and Inventory',
        metrics: {
            identityPlatforms: { value: 0, label: 'Identity Platforms' },
            unifiedAccounts: { value: 0, label: 'Unified Accounts' },
            authentications: { value: 0, label: 'Authentications' }
        },
        info: {
            summary: 'Gain unified visibility across your hybrid IAM infrastructure.',
            description:
                'Map and analyze identities, groups, permissions and more across all IAM silos, from on-prem AD to cloud IdPs and SaaS apps, to achieve full observability, governance, and compliance.',
            funFact: "Over 60% of organizations don't have sufficient visibility into their identities",
            url: '' // no URL provided for Identity Graph and Inventory
        }
    },
    [ModuleType.accessAnalysis]: {
        label: 'Access Intelligence',
        color: '#F78536',
        isDisabled: false,
        title: 'Access Intelligence',
        metrics: {
            resources: { value: 0, label: 'Resources' },
            applications: { value: 0, label: 'Applications' },
            staleAccess: { value: 0, label: 'Stale Access' }
        },
        info: {
            summary: 'Understand which users access which resources, at scale.',
            description:
                'Map all user access automatically, including sources, destinations and frequency. Use our insights to achieve Least Privilege access, assist in app migration projects, and save money by finding unused apps.',
            funFact: "Over 70% of employees have access rights that they don't actually use",
            url: '' // missing url jira task - AF-40854
        }
    },
    [ModuleType.ispm]: {
        label: 'ISPM',
        color: '#DF951E',
        isDisabled: false,
        title: 'Identity Security Posture Management (ISPM)',
        metrics: {
            securityExposures: { value: 0, label: 'Security Exposures' },
            staleUsers: { value: 0, label: 'Stale Users' },
            criticalExposureEntities: { value: 0, label: 'Entities at Critical Exposure' }
        },
        info: {
            summary: 'Find and fix exposures to reduce your identity attack surface.',
            description:
                "You can't protect what you can't see. Improve your resilience with continuous discovery and mitigation of identity exposures such as vulnerable protocols, stale accounts and shadow admins.",
            funFact: '37% of admin users regularly authenticate with NTLM, a vulnerable protocol',
            url: 'https://www.silverfort.com/ispm-signup/'
        }
    },
    [ModuleType.itdr]: {
        label: 'ITDR',
        color: '#DC981B',
        isDisabled: false,
        title: 'Identity Threat Detection & Response (ITDR)',
        metrics: {
            incidents: { value: 0, label: 'Incidents' },
            resolvedIncidents: { value: 0, label: 'Resolved Incidents' },
            criticalRiskUsers: { value: 0, label: 'Users at Critical Risk' }
        },
        info: {
            summary: 'Detect and respond to identity threats anywhere, faster.',
            description:
                'Rapidly detect account takeover, lateral movement, ransomware propagation and more, both on-prem and in the cloud. Leverage unique inline response capabilities to stop threats before they can spread.',
            funFact: 'Only 22.4% of organizations believe that they can detect and stop lateral movement',
            url: 'https://www.silverfort.com/itdr-signup/'
        }
    }
};

export const IntegrationsList: Record<IntegrationType, IntegrationData> = {
    [IntegrationType.onPrem]: {
        category: 'On-prem IAM',
        providers: {},
        addUrl: '/Settings/Integrations'
    },
    [IntegrationType.idp]: {
        category: 'Cloud IdPs',
        providers: {},
        addUrl: '/Settings/Cloud%20Integrations'
    },
    [IntegrationType.iaas]: {
        category: 'IaaS',
        providers: {},
        addUrl: '/Settings/Cloud%20Integrations'
    },
    [IntegrationType.saas]: {
        category: 'SaaS Applications',
        providers: {},
        addUrl: '/Settings/Cloud%20Integrations'
    }
};

export const ON_PREM_PROVIDER_TO_TAB: Record<string, string> = {
    ActiveDirectory: '/Settings/Integrations#adfsFederatedApps',
    PingId: '/Settings/Integrations#pingFederatedApps',
    Radius: '/Settings/General#radiusClients'
};
